/**
 * The noise floor — the sound of an empty band.
 *
 * Preferred path is the AudioWorklet in `worklets/noise-processor.js` (audio-thread synthesis,
 * no buffers, fully automatable). Where worklets are unavailable the same three colours are
 * pre-rendered into long buffers and looped; it costs a little memory and loses sample-accurate
 * automation, but the game still runs.
 */
import { audioContext, rampTo } from './context.js';
import noiseWorkletUrl from './worklets/noise-processor.js?url';

export interface NoiseLevels {
  white: number;
  pink: number;
  brown: number;
}

export interface NoiseSource {
  readonly node: AudioNode;
  readonly mode: 'worklet' | 'buffer';
  setLevels(levels: Partial<NoiseLevels>, seconds?: number): void;
  setGain(value: number, seconds?: number): void;
  levels(): NoiseLevels;
  dispose(): void;
}

export const DEFAULT_LEVELS: NoiseLevels = { white: 0.35, pink: 1, brown: 0.5 };

/** Seconds of pre-rendered noise in the fallback path — long enough that the loop isn't audible. */
const FALLBACK_SECONDS = 11;

let workletReady: Promise<boolean> | null = null;

async function ensureWorklet(ctx: AudioContext): Promise<boolean> {
  if (!workletReady) {
    workletReady = (async () => {
      if (!ctx.audioWorklet) return false;
      try {
        await ctx.audioWorklet.addModule(noiseWorkletUrl);
        return true;
      } catch {
        return false;
      }
    })();
  }
  return workletReady;
}

export async function createNoiseSource(
  levels: NoiseLevels = DEFAULT_LEVELS,
): Promise<NoiseSource> {
  const ctx = audioContext();
  return (await ensureWorklet(ctx)) ? workletSource(ctx, levels) : bufferSource(ctx, levels);
}

function workletSource(ctx: AudioContext, initial: NoiseLevels): NoiseSource {
  const node = new AudioWorkletNode(ctx, 'nb-noise', {
    numberOfInputs: 0,
    numberOfOutputs: 1,
    outputChannelCount: [2],
  });

  const params = {
    white: node.parameters.get('white')!,
    pink: node.parameters.get('pink')!,
    brown: node.parameters.get('brown')!,
    gain: node.parameters.get('gain')!,
  };

  const current: NoiseLevels = { ...initial };
  for (const key of ['white', 'pink', 'brown'] as const) params[key].value = current[key];
  params.gain.value = 1;

  return {
    node,
    mode: 'worklet',
    levels: () => ({ ...current }),
    setLevels: (next, seconds = 0.08) => {
      for (const key of ['white', 'pink', 'brown'] as const) {
        const value = next[key];
        if (value === undefined) continue;
        current[key] = value;
        rampTo(params[key], value, seconds);
      }
    },
    setGain: (value, seconds = 0.08) => rampTo(params.gain, value, seconds),
    dispose: () => {
      node.port.postMessage({ type: 'stop' });
      node.disconnect();
    },
  };
}

/**
 * Pre-render one colour of noise into a stereo buffer (two independent generator states).
 * The trims here mirror the worklet's loudness matching — keep the two in step.
 */
function renderNoiseBuffer(ctx: AudioContext, colour: keyof NoiseLevels): AudioBuffer {
  const length = Math.floor(ctx.sampleRate * FALLBACK_SECONDS);
  const buffer = ctx.createBuffer(2, length, ctx.sampleRate);

  for (let channel = 0; channel < 2; channel += 1) {
    const data = buffer.getChannelData(channel);
    let b0 = 0;
    let b1 = 0;
    let b2 = 0;
    let b3 = 0;
    let b4 = 0;
    let b5 = 0;
    let b6 = 0;
    let brown = 0;

    for (let i = 0; i < length; i += 1) {
      const w = Math.random() * 2 - 1;
      if (colour === 'white') {
        data[i] = w * 0.26;
      } else if (colour === 'pink') {
        b0 = 0.99886 * b0 + w * 0.0555179;
        b1 = 0.99332 * b1 + w * 0.0750759;
        b2 = 0.969 * b2 + w * 0.153852;
        b3 = 0.8665 * b3 + w * 0.3104856;
        b4 = 0.55 * b4 + w * 0.5329522;
        b5 = -0.7616 * b5 - w * 0.016898;
        data[i] = (b0 + b1 + b2 + b3 + b4 + b5 + b6 + w * 0.5362) * 0.082;
        b6 = w * 0.115926;
      } else {
        brown = 0.985 * brown + w * 0.02;
        data[i] = brown * 2.1;
      }
    }
  }

  return buffer;
}

function bufferSource(ctx: AudioContext, initial: NoiseLevels): NoiseSource {
  const output = ctx.createGain();
  const current: NoiseLevels = { ...initial };
  const gains: Record<keyof NoiseLevels, GainNode> = {} as Record<keyof NoiseLevels, GainNode>;
  const sources: AudioBufferSourceNode[] = [];

  for (const colour of ['white', 'pink', 'brown'] as const) {
    const source = ctx.createBufferSource();
    source.buffer = renderNoiseBuffer(ctx, colour);
    source.loop = true;

    const gain = ctx.createGain();
    gain.gain.value = current[colour];
    source.connect(gain).connect(output);
    source.start(0, Math.random() * FALLBACK_SECONDS); // stagger the loop points
    gains[colour] = gain;
    sources.push(source);
  }

  return {
    node: output,
    mode: 'buffer',
    levels: () => ({ ...current }),
    setLevels: (next, seconds = 0.08) => {
      for (const colour of ['white', 'pink', 'brown'] as const) {
        const value = next[colour];
        if (value === undefined) continue;
        current[colour] = value;
        rampTo(gains[colour].gain, value, seconds);
      }
    },
    setGain: (value, seconds = 0.08) => rampTo(output.gain, value, seconds),
    dispose: () => {
      for (const source of sources) {
        try {
          source.stop();
        } catch {
          /* already stopped */
        }
      }
      output.disconnect();
    },
  };
}
